import { useQuery } from '@tanstack/react-query';
import { OUTCOME_SELECT, parseItemJudgments, parseJudgmentOverview } from '@cerebro-diem/core';
import type { Outcome } from '@cerebro-diem/core';
import { queryKeys } from './queries';
import { supabase } from './supabase';

/** Every outcome of the signed-in user, active and retired (CER-179); the pages rank and split them with core's rules. */
export function useOutcomes() {
  return useQuery({
    queryKey: queryKeys.outcomes,
    queryFn: async () => {
      const { data, error } = await supabase.from('outcomes').select(OUTCOME_SELECT).order('position');
      if (error) throw error;
      return (data ?? []) as Outcome[];
    },
  });
}

/** Add an outcome at the end of the list. The database places it; the position sent is only a placeholder. */
export async function addOutcome(title: string, description: string): Promise<void> {
  const { error } = await supabase.from('outcomes').insert({ title: title.trim(), description: description.trim(), position: 1 });
  if (error) throw error;
}

/** Change one outcome. Throws when nothing was changed, so a silent no-op is never shown as saved. */
async function updateOutcome(id: string, update: Record<string, unknown>): Promise<void> {
  const { data, error } = await supabase.from('outcomes').update(update).eq('id', id).select('id');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('outcome not found; the list may have changed');
}

export const rewordOutcome = (id: string, title: string, description: string) =>
  updateOutcome(id, { title: title.trim(), description: description.trim() });

export const retireOutcome = (id: string) => updateOutcome(id, { retired_at: new Date().toISOString() });

/** Set the whole order: every active outcome's id, first to last. */
export async function reorderOutcomes(ids: string[]): Promise<void> {
  const { error } = await supabase.rpc('reorder_outcomes', { p_ids: ids });
  if (error) throw error;
}

/**
 * How the scoring of the signed-in user's items against their outcomes
 * stands (judgment_overview, CER-180). Asked again every few seconds while
 * anything is still being scored, so the page shows it finish.
 */
export function useJudgmentOverview() {
  return useQuery({
    queryKey: queryKeys.judgmentOverview,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('judgment_overview');
      if (error) throw error;
      return parseJudgmentOverview(data);
    },
    staleTime: 0,
    refetchInterval: (query) => ((query.state.data?.waiting ?? 0) > 0 ? 5000 : false),
  });
}

/**
 * Every stored answer about the signed-in user's tasks (my_item_judgments,
 * CER-181), from which Today's order is made. One value, so a few hundred
 * tasks' answers are never cut off at the API's row limit.
 */
export function useTaskJudgments() {
  return useQuery({
    queryKey: queryKeys.taskJudgments,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('my_item_judgments', { p_item_type: 'task' });
      if (error) throw error;
      return parseItemJudgments(data);
    },
  });
}
